import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, Input, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { SendIcon } from "../../../components/icons";
import { useCustomers } from "../hooks";
import { useCategories } from "../../categories/hooks";
import { cleanObject } from "../../../helpers/clean-object";

interface ISearchForm {
  search?: string;
  isComplete?: string;
  email?: string;
  siren?: string;
  categoryId?: string;
}

export const Customers: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: categoriesData } = useCategories();
  const categoryId = searchParams.get("categoryId");

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      search: searchParams.get("search") ?? "",
      isComplete: searchParams.get("isComplete") ?? "",
      email: searchParams.get("email") ?? "",
      siren: searchParams.get("siren") ?? "",
      categoryId: searchParams.get("categoryId") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/customers",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const where: any = {};
  const search = searchParams.get("search");
  const isComplete = searchParams.get("isComplete");
  const email = searchParams.get("email");
  const siren = searchParams.get("siren");
  if (search) where.search = search;
  if (categoryId) where.categoryId = +categoryId;
  if (isComplete) where.isComplete = isComplete === "true";
  if (email) where.email = email;
  if (siren) where.siren = siren;

  const { data, loading } = useCustomers({ where });

  return (
    <>
      <Header
        title="Clients"
        subtitle={
          categoryId ? "Clients filtrés par catégorie" : "Liste des clients"
        }
        buttons={[
          {
            actionText: "Nouveau client",
            bgColor: "indigo",
            textColor: "white",
            link: `/customer/create`,
            icon: <SendIcon />,
          },
        ]}
      />

      <div className="main-container">
        {categoryId && (
          <div className="card mb-4 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Filtré par catégorie
            </span>
            <span
              className="text-indigo-600 text-sm cursor-pointer hover:underline"
              onClick={() => navigate("/customers")}
            >
              Retirer le filtre
            </span>
          </div>
        )}

        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder="Rechercher un client..."
          advancedFilters={
            <>
              <Select label="Catégorie" className="w-full" {...form.register("categoryId")}>
                <option value="">Toutes</option>
                {categoriesData?.categories?.results?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
              <Select label="Fiche" className="w-full" {...form.register("isComplete")}>
                <option value="">Toutes</option>
                <option value="true">Complètes</option>
                <option value="false">Incomplètes</option>
              </Select>
              <Input
                label="Email"
                className="w-full"
                {...form.register("email")}
                placeholder="contact@client.fr"
              />
              <Input
                label="Siren"
                className="w-full"
                maxLength={9}
                {...form.register("siren")}
                placeholder="123456789"
              />
            </>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !data?.customers?.results?.length ? (
            <EmptyList text="Aucun client" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Nom</TableCell>
                  <TableCell header align="center">
                    Prix seul
                  </TableCell>
                  <TableCell header align="center">
                    Fiche complète
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.customers?.results?.map((customer) => (
                  <TableRow
                    key={`customer-${customer.id}`}
                    onClick={() => navigate(`/customer/${customer.id}`)}
                  >
                    <TableCell>{customer.name}</TableCell>
                    <TableCell align="center">
                      {customer.priceOnly ? "Oui" : "Non"}
                    </TableCell>
                    <TableCell align="center">
                      {customer.isComplete ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Complète
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          Incomplète
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
